import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import useTranslation from "~/app/hooks/useTranslation";
import usePageTitle from "~/app/hooks/usePageTitle";
import { useAppHeader } from "~/app/modules/appHeader";
import { useParams, useHistory, useLocation } from "react-router-dom";

import classes from "./style.module.scss";
import Modal from "~/app/common/Modal";
import SearchInput from '~/app/modules/form/Search';
import { MachineSearch, Project } from '~/types';
import { API } from '~/app/routes';
import { toast } from "react-toastify";
import { useStoreActions, useStoreState } from "~/store";
import { MANUAL_TIME_ENTRY_KIND, ManualTimeEntryKind } from "~/timeManagement/manualEntryKind";
import { TIME_MODE_TYPE, TimeModeType } from "~/timeManagement/timeModeType";
import { TIME_MANAGEMENT_ROUTES } from '~/timeManagement/Router';
import { route } from "~/app/helpers/route";
import { formatMachineSearchLabel, formatMachineSearchValue } from "~/machines/helpers";

const OTHER_ABSENCE_NAME_KEY = 'timeManagement.absence.other_absence';

const isOtherAbsenceOption = (row: { name: string; is_other?: boolean }) =>
  !!row.is_other || row.name === OTHER_ABSENCE_NAME_KEY;

interface Activity {
  id: number;
  name: string;
  type?: number | null;
  status: number;
  absence_type?: string | null;
}

interface Task {
  id: number;
  project_number: string | number;
  project_id: number;
  project_name?: string;
  activity_name?: string;
  activity_id?: number;
  machine_intern?: string | number;
  machine_id?: number;
  machine_name?: string;
  machine_reg_no?: string;
  machine_serial_no?: string;
  total_time: string;
  total_minutes: number;
  mode_type: TimeModeType;
  notes?: string;
  date?: string;
}

interface LocationState {
  selectedDate?: string;
  entryKind?: ManualTimeEntryKind;
  taskData?: Task;
  returnTo?: string;
  fromActiveTime?: boolean;
  timeManagementId?: number;
  editAbsence?: {
    absence_registration_id: number;
    absence_id?: number;
    hours_decimal?: number;
    notes?: string;
    other_absence_reason?: string;
    date?: string;
  };
}

const AddForm: React.FC = () => {
  const { setData, clearData } = useAppHeader();
  const { t } = useTranslation('timeManagement');
  const { t: translateLabel } = useTranslation();
  const history = useHistory();
  const location = useLocation<LocationState>();
  const { id } = useParams<{ id?: string }>();
  const currentUserId = useStoreState((state) => state.user.data.id);
  const standardWorkingHoursPerDay = useStoreState(
    (state) => state.machines.standardWorkingHoursPerDay
  );

  const isEditMode = !!id;
  const returnTo = location.state?.returnTo;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [date, setDate] = useState(
    location.state?.selectedDate || new Date().toISOString().split('T')[0]
  );
  const [project, setProject] = useState<Project>();
  const [machine, setMachine] = useState<MachineSearch>();
  const [mode, setMode] = useState<TimeModeType>(TIME_MODE_TYPE.MACHINE);
  const [hours, setHours] = useState(7);
  const [minutes, setMinutes] = useState(30);
  const [note, setNote] = useState("");
  const [forceUpdate, setForceUpdate] = useState(0);
  const [status, setStatus] = useState<0 | 1 | 2>(1);
  const initialManualKind =
    location.state?.editAbsence != null
      ? MANUAL_TIME_ENTRY_KIND.ABSENCE
      : location.state?.entryKind === MANUAL_TIME_ENTRY_KIND.ABSENCE
        ? MANUAL_TIME_ENTRY_KIND.ABSENCE
        : MANUAL_TIME_ENTRY_KIND.WORK;
  const [manualKind, setManualKind] = useState<ManualTimeEntryKind>(initialManualKind);
  const [absenceOptions, setAbsenceOptions] = useState<{ id: number; name: string; is_other?: boolean }[]>([]);
  const [loadingAbsenceTypes, setLoadingAbsenceTypes] = useState(false);
  const absenceTypesLoadInFlightRef = useRef(false);
  const absenceTypesLoadedRef = useRef(false);
  const absenceEditFetchedIdRef = useRef<number | null>(null);
  const [absenceId, setAbsenceId] = useState<number | null>(null);
  const [absenceHours, setAbsenceHours] = useState<number>(standardWorkingHoursPerDay);
  const [absenceToDate, setAbsenceToDate] = useState(
    location.state?.selectedDate || new Date().toISOString().split('T')[0]
  );
  const [editAbsenceRegId, setEditAbsenceRegId] = useState<number | null>(null);
  const [absenceWorkConfirmOpen, setAbsenceWorkConfirmOpen] = useState(false);
  const pendingWorkSaveRef = useRef<(() => void) | null>(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  usePageTitle(editAbsenceRegId ? t('editAbsence') : isEditMode ? t('editTime') : t('addTime'));
  
  // Activity states
  const [activities, setActivities] = useState<Activity[]>([]);
  const [selectedActivity, setSelectedActivity] = useState<number | null>(null);

  const saveTime = useStoreActions(actions => actions.timeManagement.save);
  const saveAbsence = useStoreActions(actions => actions.timeManagement.saveAbsence);
  const deleteAbsence = useStoreActions(actions => actions.timeManagement.deleteAbsence);
  const getAbsenceTypes = useStoreActions(actions => actions.timeManagement.getAbsenceTypes);
  const getAbsenceById = useStoreActions(actions => actions.timeManagement.getAbsenceById);
  const getAbsenceTypesRef = useRef(getAbsenceTypes);
  getAbsenceTypesRef.current = getAbsenceTypes;
  const getAbsenceByIdRef = useRef(getAbsenceById);
  getAbsenceByIdRef.current = getAbsenceById;
  const employeeHasAbsenceOnDate = useStoreActions(
    actions => actions.timeManagement.employeeHasAbsenceOnDate
  );
  const ActivitiesListAction = useStoreActions(actions => actions.timeManagement.getAllActivitiesData);
  const TimeManagementByIdAction = useStoreActions(actions => actions.timeManagement.getTimeManagementById);
  const DeleteAction = useStoreActions(actions => actions.timeManagement.delete);


useEffect(() => {
  let cancelled = false;

  const fetchActivities = async () => {
    try {
      const res = await ActivitiesListAction({});
      if (cancelled) {
        return;
      }
      let dataArray: Activity[] = [];
      if (res.data && Array.isArray(res.data)) {
        dataArray = res.data as Activity[];
      } else if (res.data && res.data.data && Array.isArray(res.data.data)) {
        dataArray = res.data.data as Activity[];
      } else if (Array.isArray(res)) {
        dataArray = res as Activity[];
      }
      setActivities(dataArray);
    } catch (error) {
      if (!cancelled) {
        toast.error(t('couldNotFetchActivities'));
      }
    }
  };

  fetchActivities();

  return () => {
    cancelled = true;
  };
  // Intentionally run once on mount: this thunk toggles timeManagement.fetching, which can
  // change useStoreActions references and retrigger the effect if listed as a dependency.
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, []);

  useEffect(() => {
    if (manualKind !== MANUAL_TIME_ENTRY_KIND.ABSENCE) {
      absenceTypesLoadedRef.current = false;
      return;
    }

    let cancelled = false;

    const run = async () => {
      if (absenceTypesLoadInFlightRef.current || absenceTypesLoadedRef.current) {
        return;
      }
      absenceTypesLoadInFlightRef.current = true;
      if (!cancelled) {
        setLoadingAbsenceTypes(true);
      }
      try {
        const res: any = await getAbsenceTypesRef.current();
        if (cancelled) {
          return;
        }
        const list =
          res?.result === 'success' && Array.isArray(res.data)
            ? res.data
            : Array.isArray(res)
              ? res
              : Array.isArray(res?.data)
                ? res.data
                : [];
        setAbsenceOptions(
          list.map((row: any) => {
            const name = String(row.name || '');
            return {
              id: Number(row.id),
              name,
              is_other: isOtherAbsenceOption({ name, is_other: row.is_other }),
            };
          })
        );
        absenceTypesLoadedRef.current = true;
      } catch {
        if (!cancelled) {
          toast.error(t('couldNotFetchAbsenceTypes'));
        }
      } finally {
        if (!cancelled) {
          setLoadingAbsenceTypes(false);
        }
        absenceTypesLoadInFlightRef.current = false;
      }
    };

    run();

    return () => {
      cancelled = true;
    };
    // Only re-run when switching to/from absence — not when loadAbsenceTypes identity changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manualKind]);

  useEffect(() => {
    if (editAbsenceRegId) {
      return;
    }
    if (manualKind !== MANUAL_TIME_ENTRY_KIND.ABSENCE) {
      return;
    }
    setAbsenceHours(standardWorkingHoursPerDay);
  }, [manualKind, standardWorkingHoursPerDay, editAbsenceRegId]);

  const applyAbsenceEditData = (data: Record<string, unknown>) => {
    const regId = Number(data.absence_registration_id ?? data.id);
    if (Number.isFinite(regId) && regId > 0) {
      setEditAbsenceRegId(regId);
    }
    if (data.date) {
      setDate(String(data.date));
    }
    if (data.absence_id != null && data.absence_id !== '') {
      setAbsenceId(Number(data.absence_id));
    }
    const hd = Number(data.hours_decimal ?? data.hours);
    setAbsenceHours(!Number.isNaN(hd) && hd > 0 ? hd : standardWorkingHoursPerDay);
    const storedNotes = String(data.notes || '').trim();
    const legacyReason = String(data.other_absence_reason || '').trim();
    setNote(storedNotes || legacyReason);
  };

  useEffect(() => {
    const regId = location.state?.editAbsence?.absence_registration_id;
    if (!regId) {
      return;
    }
    const id = Number(regId);
    if (!Number.isFinite(id) || id < 1) {
      return;
    }
    if (absenceEditFetchedIdRef.current === id) {
      return;
    }
    absenceEditFetchedIdRef.current = id;

    let cancelled = false;
    const fallbackEdit = location.state?.editAbsence;

    const loadAbsenceForEdit = async () => {
      setLoading(true);
      setEditAbsenceRegId(id);
      setManualKind(MANUAL_TIME_ENTRY_KIND.ABSENCE);

      try {
        const res: any = await getAbsenceByIdRef.current(id);
        if (cancelled) {
          return;
        }
        if (res?.result === 'success' && res.data) {
          applyAbsenceEditData(res.data);
        } else if (fallbackEdit) {
          applyAbsenceEditData(fallbackEdit as Record<string, unknown>);
        }
      } catch {
        if (!cancelled && fallbackEdit) {
          applyAbsenceEditData(fallbackEdit as Record<string, unknown>);
        } else if (!cancelled) {
          toast.error(t('couldNotFetchData'));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadAbsenceForEdit();

    return () => {
      cancelled = true;
    };
    // Do not list `t` or applyAbsenceEditData — `t` changes every render and caused infinite GETs
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state?.editAbsence?.absence_registration_id]);

  const isSelectedAbsenceOther = useMemo(() => {
    if (absenceId == null) return false;
    const opt = absenceOptions.find((a) => a.id === Number(absenceId));
    if (!opt) return false;
    return isOtherAbsenceOption(opt);
  }, [absenceId, absenceOptions]);

  /** Multi-day absence range is only offered when creating new absence entries. */
  const isAbsenceRange = useMemo(
    () => manualKind === MANUAL_TIME_ENTRY_KIND.ABSENCE && !editAbsenceRegId,
    [manualKind, editAbsenceRegId]
  );

  const isAbsenceDateRangeInvalid = useMemo(() => {
    if (!isAbsenceRange || !date || !absenceToDate) {
      return false;
    }
    return absenceToDate < date;
  }, [isAbsenceRange, date, absenceToDate]);

  const onAbsenceFromDateChange = (nextDate: string) => {
    setDate(nextDate);
    if (isAbsenceRange && (!absenceToDate || absenceToDate < nextDate)) {
      setAbsenceToDate(nextDate);
    }
  };

  const isHoursInvalid = useCallback(() => {
    if (manualKind !== MANUAL_TIME_ENTRY_KIND.WORK) {
      return false;
    }
    const n = Number(hours);
    return Number.isNaN(n) || !Number.isInteger(n) || n < 0 || n > 24;
  }, [manualKind, hours]);

  const isMinutesInvalid = useCallback(() => {
    if (manualKind !== MANUAL_TIME_ENTRY_KIND.WORK) {
      return false;
    }
    const n = Number(minutes);
    return Number.isNaN(n) || !Number.isInteger(n) || n < 0 || n > 59;
  }, [manualKind, minutes]);

  /** 24:00 is allowed; anything above (e.g. 24:30) is not. */
  const isTotalTimeInvalid = useCallback(() => {
    if (manualKind !== MANUAL_TIME_ENTRY_KIND.WORK) {
      return false;
    }
    if (isHoursInvalid() || isMinutesInvalid()) {
      return false;
    }
    const totalMinutes = Number(hours) * 60 + Number(minutes);
    return totalMinutes > 24 * 60;
  }, [manualKind, hours, minutes, isHoursInvalid, isMinutesInvalid]);

  const isWorkDurationEmpty = useCallback(() => {
    if (manualKind !== MANUAL_TIME_ENTRY_KIND.WORK) {
      return false;
    }
    if (isHoursInvalid() || isMinutesInvalid()) {
      return false;
    }
    return Number(hours) === 0 && Number(minutes) === 0;
  }, [manualKind, hours, minutes, isHoursInvalid, isMinutesInvalid]);

  const showTimeValidationError = useCallback(
    (msgKey: string, fallbackKey: string) => {
      toast.error(
        translateLabel(msgKey, {
          defaultValue: t(fallbackKey),
          max: standardWorkingHoursPerDay,
        })
      );
    },
    [translateLabel, t, standardWorkingHoursPerDay]
  );

  const filteredActivities = useMemo(() => {
    const filtered = activities.filter(activity => {
      
      if (activity.status !== 1) {
        return false;
      }
      if (activity.absence_type != null && String(activity.absence_type).trim() !== '') {
        return false;
      }
      if (activity.type === null || activity.type === undefined) {
        return false;
      }
      if (mode === TIME_MODE_TYPE.MACHINE) {
        const match = activity.type === 0 || activity.type === 2;
        return match;
      } 
      else if (mode === TIME_MODE_TYPE.TASK) {
        const match = activity.type === 1 || activity.type === 2;
        return match;
      } 
      else {
        return false;
      }
    });
    return filtered;
  }, [activities, mode]);

  useEffect(() => {
    if (selectedActivity !== null) {
      const isActivityValid = filteredActivities.some(
        activity => activity.id === selectedActivity
      );
      
      if (!isActivityValid) {
        setSelectedActivity(null);
      }
    }
  }, [mode, filteredActivities, selectedActivity]);

  const handleModeChange = (newMode: TimeModeType) => {
    setMode(newMode);
    
    if (newMode === TIME_MODE_TYPE.UNSPECIFIED) {
      setSelectedActivity(null);
      setMachine(undefined);
    }
    else if (newMode === TIME_MODE_TYPE.TASK) {
      setMachine(undefined);
    }
  };

  const fetchEntryData = useCallback(async (entryId: string, cancelled?: () => boolean) => {
    if (!isMountedRef.current) {
      return;
    }
    setLoading(true);
    try {
      const response = await TimeManagementByIdAction(entryId);
      if (cancelled?.()) {
        return;
      }
      const data = response.data.data || response.data || response;

      if (!data || !data.id) {
        throw new Error("No data returned from API");
      }

      setDate(data.date || new Date().toISOString().split('T')[0]);

      if (data.project_id) {
        const projectData: Project = {
          id: String(data.project_id),
          number: data.project_number || '',
          name: data.project_name || ''
        };
        setProject(projectData);
      }

      if (data.machine_id) {
        const machineData: MachineSearch = {
          id: data.machine_id,
          intern: data.machine_intern ?? data.intern ?? "",
          name: data.machine_name ?? data.name ?? "",
          regNo: data.machine_reg_no ?? data.machine_regno ?? data.regNo ?? "",
          serialNo: data.machine_serial_no ?? data.machine_serialno ?? data.serialNo ?? "",
        };
        setMachine(machineData);
      }

      setMode((data.mode_type ?? TIME_MODE_TYPE.MACHINE) as TimeModeType);

      if (returnTo === 'dailySummary' && data.total_time !== undefined) {
        const totalMinutes = data.total_time;
        const h = Math.floor(totalMinutes / 60);
        const m = totalMinutes % 60;
        setHours(h);
        setMinutes(m);
      } else if (data.time) {
        const [h, m] = data.time.split(':').map((t: string) => parseInt(t) || 0);
        setHours(h);
        setMinutes(m);
      }
  
      setNote(data.notes || "");
      
      if (data.activity_id) {
        setSelectedActivity(data.activity_id);
      }
  
      if (data.status !== undefined) {
        setStatus(data.status);
      }
      
      setForceUpdate(prev => prev + 1);
    } catch (error) {
      if (!cancelled?.()) {
        console.error("Error fetching entry data:", error);
        toast.error(t('couldNotFetchData'));
      }
    } finally {
      if (!cancelled?.() && isMountedRef.current) {
        setLoading(false);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [TimeManagementByIdAction, returnTo]);

  useEffect(() => {
    if (!isEditMode || !id) {
      return;
    }

    let cancelled = false;
    const isCancelled = () => cancelled;

    setManualKind(MANUAL_TIME_ENTRY_KIND.WORK);
    setEditAbsenceRegId(null);
    if (location.state?.taskData) {
      const data = location.state.taskData;
      const hasRequiredFields = data.project_id && data.project_name;
      if (hasRequiredFields) {
        setDate(data.date || new Date().toISOString().split('T')[0]);

        if (data.project_id) {
          const projectData: Project = {
            id: String(data.project_id),
            number: String(data.project_number || ''),
            name: data.project_name || ''
          };
          setProject(projectData);
        }

        if (data.machine_id) {
          const machineData: MachineSearch = {
            id: String(data.machine_id),
            intern: String(data.machine_intern || ""),
            name: data.machine_name || "",
            regNo: data.machine_reg_no || "",
            serialNo: data.machine_serial_no || "",
          };
          setMachine(machineData);
        }

        setMode((data.mode_type ?? TIME_MODE_TYPE.MACHINE) as TimeModeType);

        if (data.total_time) {
          const [h, m] = data.total_time.split(':').map((t: string) => parseInt(t) || 0);
          setHours(h);
          setMinutes(m);
        }

        setNote(data.notes || "");

        if (data.activity_id) {
          setSelectedActivity(data.activity_id);
        }

        setForceUpdate(prev => prev + 1);
        setLoading(false);
        return () => {
          cancelled = true;
        };
      }
    }

    fetchEntryData(id, isCancelled);

    return () => {
      cancelled = true;
    };
  }, [id, isEditMode, fetchEntryData, location.state]);

  useEffect(() => {
    const backLink = returnTo === 'dailySummary' 
      ? route(TIME_MANAGEMENT_ROUTES.dailySummary) 
      : "/timeManagement/calender";
      
    setData({
      title: editAbsenceRegId ? t('editAbsence') : isEditMode ? t('editTime') : t('addTime'),
      subTitle: "",
      backLink: backLink,
    });
    return () => clearData();
  }, [setData, clearData, isEditMode, editAbsenceRegId, t, returnTo]);

  const handleProjectSelect = (item: Project) => {
    setProject(item);
    setForceUpdate(prev => prev + 1);
  };

  const handleMachineSelect = (item: MachineSearch) => {
    if (!item || !item.intern || !item.name) {
      toast.error(t('invalidMachineSelected'));
      return;
    }

    setMachine(item);
    setForceUpdate(prev => prev + 1);
  };

  const getProjectDisplayValue = () => {
    return project;
  };

  const getMachineDisplayValue = () => {
    return machine;
  };

  const saveData = useCallback(async (payload) => {
    if (saving) return;
    
    setSaving(true);
    try {
      await saveTime(payload);
      toast.success(isEditMode ? t('timeUpdated') : t('timeSaved'));
      history.push({
        pathname: "/timeManagement/calender",
        state: { refresh: true, timestamp: Date.now() }
      });
    } catch(e) {
      console.error(e);
    } finally {
      if (isMountedRef.current) {
        setSaving(false);
      }
    }
  }, [saveTime, isEditMode, history, t, saving]);

  const handleSave = async () => {
    if (saving) return;

    if (manualKind === MANUAL_TIME_ENTRY_KIND.ABSENCE) {
      if (!absenceId) {
        toast.error(t('absenceTypeRequired'));
        return;
      }
      const h = Number(absenceHours);
      if (Number.isNaN(h) || h <= 0) {
        toast.error(t('absenceHoursInvalid'));
        return;
      }
      if (h > standardWorkingHoursPerDay) {
        toast.error(t('absenceExceedsStandardHours', { max: standardWorkingHoursPerDay }));
        return;
      }
      const trimmedNote = (note || '').trim();
      if (isSelectedAbsenceOther && trimmedNote.length === 0) {
        toast.error(t('otherAbsenceReasonRequired'));
        return;
      }
      if (isAbsenceRange && !absenceToDate) {
        toast.error(t('invalidDateRange'));
        return;
      }
      if (isAbsenceDateRangeInvalid) {
        toast.error(t('invalidDateRange'));
        return;
      }
      setSaving(true);
      const toDateStr = isAbsenceRange ? absenceToDate : date;
      const payload: Record<string, unknown> = {
        date,
        from_date: date,
        to_date: toDateStr,
        absence_id: absenceId,
        hours: h,
        notes: trimmedNote,
      };
      const empId = Number(currentUserId);
      if (Number.isFinite(empId) && empId > 0) {
        payload.employee_id = empId;
      }
      if (editAbsenceRegId) {
        payload.id = editAbsenceRegId;
      }
      try {
        const res: any = await saveAbsence(payload);
        if (res?.result === 'success' || res?.success === true) {
          const msgKey = res?.msg || res?.message;
          const defaultSuccess = editAbsenceRegId
            ? t('absenceUpdated')
            : (Number(res?.saved_count) > 1 ? t('absenceSavedMultiple') : t('absenceSaved'));
          toast.success(
            msgKey
              ? translateLabel(msgKey, { defaultValue: defaultSuccess })
              : defaultSuccess
          );
          history.push({
            pathname: "/timeManagement/calender",
            state: { refresh: true, timestamp: Date.now() },
          });
        } else {
          const msgKey = res?.msg || res?.message;
          const maxHours = res?.max_hours ?? standardWorkingHoursPerDay;
          toast.error(
            msgKey
              ? translateLabel(msgKey, {
                  defaultValue: t('couldNotSaveAbsence'),
                  max: maxHours,
                })
              : t('couldNotSaveAbsence')
          );
        }
      } catch (err: any) {
        if (isMountedRef.current) {
          const msgKey = err?.response?.data?.msg || err?.response?.data?.message;
          const maxHours = err?.response?.data?.max_hours ?? standardWorkingHoursPerDay;
          toast.error(
            msgKey
              ? translateLabel(msgKey, {
                  defaultValue: t('couldNotSaveAbsence'),
                  max: maxHours,
                })
              : t('couldNotSaveAbsence')
          );
        }
      } finally {
        if (isMountedRef.current) {
          setSaving(false);
        }
      }
      return;
    }

    if (!project) {
      toast.error(t('projectRequired'));
      return;
    }

    if (mode === TIME_MODE_TYPE.MACHINE && !machine) {
      toast.error(t('machineRequiredInMachineMode'));
      return;
    }

    if (isHoursInvalid()) {
      showTimeValidationError('activity.validation.hoursRange', 'hoursRange');
      return;
    }
    if (isMinutesInvalid()) {
      showTimeValidationError('activity.validation.minutesRange', 'minutesRange');
      return;
    }
    if (isTotalTimeInvalid()) {
      showTimeValidationError('activity.validation.totalTimeExceeds', 'totalTimeExceeds');
      return;
    }
    if (isWorkDurationEmpty()) {
      showTimeValidationError('activity.validation.invalidWorkDuration', 'invalidWorkDuration');
      return;
    }

    const timeString = `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
    const totalMinutes = hours * 60 + minutes;

    const updatedTask = {
      id: parseInt(id || '0'),
      project_number: project.number,
      project_id: parseInt(project.id),
      project_name: project.name,
      activity_name: activities.find(a => a.id === selectedActivity)?.name,
      activity_id: selectedActivity,
      machine_intern: machine?.intern,
      machine_id: machine?.id,
      machine_name: machine?.name,
      machine_reg_no: machine?.regNo,
      machine_serial_no: machine?.serialNo,
      total_time: timeString,
      total_minutes: totalMinutes,
      mode_type: mode,
      notes: note,
      date: date,
    };
    const payload = {
      id: id ? parseInt(id) : undefined,
      date,
      project_id: parseInt(project.id),
      machine_id: machine?.id ?? null,
      time: timeString,
      mode_type: mode,
      notes: note || null,
      activity_id: selectedActivity || null,
      status: status,
      total_time: totalMinutes,
    };

    const commitWorkSave = () => {
      if (returnTo === 'dailySummary') {
        history.push({
          pathname: route(TIME_MANAGEMENT_ROUTES.dailySummary),
          state: { updatedTask },
        });
      } else {
        saveData(payload);
      }
    };

    const empIdForDay = currentUserId ? Number(currentUserId) : null;
    if (empIdForDay) {
      try {
        const res = await employeeHasAbsenceOnDate({
          employee_id: empIdForDay,
          date,
        });
        if (res?.result === 'success' && res?.data?.has_absence) {
          if (isMountedRef.current) {
            pendingWorkSaveRef.current = commitWorkSave;
            setAbsenceWorkConfirmOpen(true);
          }
          return;
        }
      } catch {
        /* if check fails, still allow save */
      }
    }

    commitWorkSave();
  };

  const cancelAbsenceWorkConfirm = () => {
    setAbsenceWorkConfirmOpen(false);
    pendingWorkSaveRef.current = null;
  };

  const confirmAbsenceWorkSave = () => {
    setAbsenceWorkConfirmOpen(false);
    const run = pendingWorkSaveRef.current;
    pendingWorkSaveRef.current = null;
    run?.();
  };

  const handleCancel = () => {
    if (returnTo === 'dailySummary') {
      history.push(route(TIME_MANAGEMENT_ROUTES.dailySummary));
    } else {
      history.push("/timeManagement/calender");
    }
  };

  const handleDelete = useCallback(async () => {
    if (saving) return;

    if (manualKind === MANUAL_TIME_ENTRY_KIND.ABSENCE && editAbsenceRegId) {
      setSaving(true);
      try {
        const delRes: any = await deleteAbsence(editAbsenceRegId);
        if (delRes?.success !== true) {
          toast.error(t('couldNotDeleteAbsence'));
          return;
        }
        toast.success(t('absenceDeleted'));
        history.push({
          pathname: "/timeManagement/calender",
          state: { refresh: true, timestamp: Date.now() },
        });
      } catch (e) {
        toast.error(t('couldNotDeleteAbsence'));
      } finally {
        if (isMountedRef.current) {
          setSaving(false);
        }
      }
      return;
    }

    if (!id) return;

  setSaving(true);
  try {
    await DeleteAction(id);
    toast.success(t('timeDeleted'));
    
    if (returnTo === 'dailySummary') {
      history.push({
        pathname: route(TIME_MANAGEMENT_ROUTES.dailySummary),
        state: { refresh: true }
      });
    } else {
      history.push("/timeManagement/calender");
    }
  } catch (e) {
    console.error(e);
  } finally {
    if (isMountedRef.current) {
      setSaving(false);
    }
  }
}, [DeleteAction, deleteAbsence, id, history, t, returnTo, saving, manualKind, editAbsenceRegId]);

  if (loading) {
    return (
      <div className={classes.wrapper}>
        <p style={{ textAlign: 'center', padding: '40px' }}>{t('loadingData')}</p>
      </div>
    );
  }

  const projectDisplayValue = getProjectDisplayValue();
  const machineDisplayValue = getMachineDisplayValue();
  
  return (
    <div className={classes.wrapper}>
      {(!isEditMode || editAbsenceRegId) && (
        <div className={classes.dateField}>
          <label className={classes.label}>
            {isAbsenceRange ? t('fromDate') : t('date')}<span className={classes.requiredMark}> *</span>
          </label>
          <div className={classes.inputBox}>
            <input
              type="date"
              value={date}
              onChange={(e) => onAbsenceFromDateChange(e.target.value)}
            />
          </div>
        </div>
      )}

      {isAbsenceRange && (
        <div className={classes.dateField}>
          <label className={classes.label}>
            {t('toDate')}<span className={classes.requiredMark}> *</span>
          </label>
          <div className={classes.inputBox}>
            <input
              type="date"
              value={absenceToDate}
              min={date}
              onChange={(e) => setAbsenceToDate(e.target.value)}
            />
          </div>
          {isAbsenceDateRangeInvalid && (
            <p className={classes.fieldError}>{t('invalidDateRange')}</p>
          )}
        </div>
      )}

      {!isEditMode && !editAbsenceRegId && (
        <div className={classes.modeSwitch} style={{ marginBottom: 8 }}>
          <button
            type="button"
            className={`${classes.modeBtn} ${manualKind === MANUAL_TIME_ENTRY_KIND.WORK ? classes.active : ''}`}
            onClick={() => setManualKind(MANUAL_TIME_ENTRY_KIND.WORK)}
            disabled={saving}
          >
            {t('entryKindWork')}
          </button>
          <button
            type="button"
            className={`${classes.modeBtn} ${manualKind === MANUAL_TIME_ENTRY_KIND.ABSENCE ? classes.active : ''}`}
            onClick={() => {
              setManualKind(MANUAL_TIME_ENTRY_KIND.ABSENCE);
              setAbsenceToDate(date);
            }}
            disabled={saving}
          >
            {t('entryKindAbsence')}
          </button>
        </div>
      )}

      {manualKind === MANUAL_TIME_ENTRY_KIND.ABSENCE && (
        <>
          <label className={classes.label}>
            {t('absenceType')}<span className={classes.requiredMark}> *</span>
          </label>
          <div className={classes.inputBox}>
            <div className={classes.selectWrapper}>
              <select
                className={classes.select}
                value={absenceId ?? ''}
                onChange={(e) => {
                  setAbsenceId(e.target.value ? Number(e.target.value) : null);
                }}
                disabled={saving || loadingAbsenceTypes}
              >
                <option value="">
                  {loadingAbsenceTypes
                    ? t('loadingData')
                    : t('selectAbsence')}
                </option>
                {absenceOptions.map((row) => (
                  <option key={row.id} value={row.id}>
                    {translateLabel(row.name, { defaultValue: row.name })}
                  </option>
                ))}
              </select>
              <svg
                className={classes.dropdownIcon}
                width="12"
                height="8"
                viewBox="0 0 12 8"
                fill="none"
              >
                <path
                  d="M1 1L6 6L11 1"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          </div>

          <label className={classes.label}>
            {t('absenceHours')}<span className={classes.requiredMark}> *</span>
          </label>
          <div className={`${classes.inputBox} ${Number(absenceHours) > standardWorkingHoursPerDay ? classes.inputBoxError : ''}`}>
            <input
              type="number"
              step={0.25}
              min={0.01}
              value={absenceHours}
              onChange={(e) => setAbsenceHours(Number(e.target.value))}
              disabled={saving}
            />
          </div>
          {Number(absenceHours) > standardWorkingHoursPerDay && (
            <p className={classes.fieldError}>
              {t('absenceExceedsStandardHours', { max: standardWorkingHoursPerDay })}
            </p>
          )}

        </>
      )}

      {manualKind === MANUAL_TIME_ENTRY_KIND.WORK && (
        <>
      <label className={classes.label}>
        {t('project')}<span className={classes.requiredMark}> *</span>
      </label>
      <div className={classes.inputBox}>
        <SearchInput<Project>
          key={`project-${forceUpdate}-${project?.id || 'empty'}`}
          placeholder={t('projectPlaceholder')}
          className={classes.field}
          inputClassName={classes.input}
          searchUrl={API.projects}
          renderItem={(item) => {
            if (item && 'result' in item && item.result === 'fail') {
              return '';
            }

            if (item && item.number !== undefined && item.name !== undefined) {
              const number = String(item.number || '');
              const name = String(item.name || '');
              return `${number} ${name}`.trim();
            }

            return '';
          }}
          onSelect={handleProjectSelect as any}
          iconPosition="left"
          defaultValue={projectDisplayValue?.id}
        />
      </div>

      <label className={classes.label}>{t('mode')}</label>
      <div className={classes.modeSwitch}>
        <button
          className={`${classes.modeBtn} ${mode === TIME_MODE_TYPE.MACHINE ? classes.active : ""}`}
          onClick={() => handleModeChange(TIME_MODE_TYPE.MACHINE)}
          disabled={saving}
        >
          {t('selectMachine')}
        </button>
        <button
          className={`${classes.modeBtn} ${mode === TIME_MODE_TYPE.TASK ? classes.active : ""}`}
          onClick={() => handleModeChange(TIME_MODE_TYPE.TASK)}
          disabled={saving}
        >
          {t('selectTask')}
        </button>
        <button
          className={`${classes.modeBtn} ${mode === TIME_MODE_TYPE.UNSPECIFIED ? classes.active : ""}`}
          onClick={() => handleModeChange(TIME_MODE_TYPE.UNSPECIFIED)}
          disabled={saving}
        >
          {t('selectUnspecified')}
        </button>
      </div>

      {mode === TIME_MODE_TYPE.MACHINE && (
        <>
          <label className={classes.label}>
            {t('machine')}<span className={classes.requiredMark}> *</span>
          </label>
          <div className={classes.inputBox}>
            <SearchInput<MachineSearch>
              key={`machine-${forceUpdate}-${machine?.id || 'empty'}`}
              placeholder={t('search')}
              className={classes.field}
              inputClassName={classes.input}
              searchUrl={API.machines}
              renderItem={(item) => {
                if (item && 'result' in item && item.result === 'fail') {
                  return ''; 
                }

                if (item && item.intern !== undefined && item.name !== undefined) {
                  return formatMachineSearchLabel(item);
                }
                return '';
              }}
              renderValue={formatMachineSearchValue}
              searchKeys={["intern", "name", "regNo", "serialNo", "assignedDriverName"]}
              onSelect={handleMachineSelect as any}
              iconPosition="left"
              defaultValue={machineDisplayValue?.id}
            />
          </div>
        </>
      )}

      {(mode === TIME_MODE_TYPE.MACHINE || mode === TIME_MODE_TYPE.TASK) && (
        <>
          <label className={classes.label}>{t('selectActivity')}</label>
          <div className={classes.inputBox}>
            <div className={classes.selectWrapper}>
              <select
                className={classes.select}
                value={selectedActivity || ''}
                onChange={(e) => setSelectedActivity(e.target.value ? Number(e.target.value) : null)}
                disabled={saving}
              >
                <option value="">{t('selectActivityPlaceholder')}</option>
                {filteredActivities.length === 0 ? (
                  <option disabled>No activities available for this mode</option>
                ) : (
                  filteredActivities.map((activity) => (
                    <option key={activity.id} value={activity.id}>
                      {activity.name}
                    </option>
                  ))
                )}
              </select>
              <svg className={classes.dropdownIcon} width="12" height="8" viewBox="0 0 12 8" fill="none">
                <path d="M1 1L6 6L11 1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
          </div>
        </>
      )}

      <label className={classes.label}>
        {t('time')}<span className={classes.requiredMark}> *</span>
      </label>
      <div className={classes.timeRow}>
        <div className={classes.timeInput}>
          <input
            type="number"
            value={hours}
            min={0}
            max={24}
            onChange={(e) => setHours(e.target.value === '' ? 0 : Number(e.target.value))}
            className={`${classes.numberInput} ${(isHoursInvalid() || isTotalTimeInvalid()) ? classes.inputInvalid : ''}`}
            disabled={saving}
          />
          <span className={classes.inputLabel}>{t('hours')}</span>
        </div>

        <div className={classes.colon}>:</div>

        <div className={classes.timeInput}>
          <input
            type="number"
            value={minutes}
            min={0}
            max={59}
            onChange={(e) => setMinutes(e.target.value === '' ? 0 : Number(e.target.value))}
            className={`${classes.numberInput} ${(isMinutesInvalid() || isTotalTimeInvalid()) ? classes.inputInvalid : ''}`}
            disabled={saving}
          />
          <span className={classes.inputLabel}>{t('minutes')}</span>
        </div>
      </div>
      {isHoursInvalid() && (
        <p className={classes.fieldError}>{t('hoursRange')}</p>
      )}
      {isMinutesInvalid() && (
        <p className={classes.fieldError}>{t('minutesRange')}</p>
      )}
      {isTotalTimeInvalid() && (
        <p className={classes.fieldError}>{t('totalTimeExceeds')}</p>
      )}
      {isWorkDurationEmpty() && (
        <p className={classes.fieldError}>{t('invalidWorkDuration')}</p>
      )}
        </>
      )}

      <label className={classes.label}>
        {t('notes')}
        {manualKind === MANUAL_TIME_ENTRY_KIND.ABSENCE && isSelectedAbsenceOther && (
          <span className={classes.requiredMark}> *</span>
        )}
      </label>
      <textarea
        className={classes.textarea}
        placeholder={
          manualKind === MANUAL_TIME_ENTRY_KIND.ABSENCE && isSelectedAbsenceOther
            ? t('notesPlaceholderOtherAbsence')
            : t('notesPlaceholder')
        }
        value={note}
        onChange={(e) => setNote(e.target.value)}
        disabled={saving}
      />

      <div className={classes.btnRow}>
        <button
          className={classes.cancel}
          onClick={handleCancel}
          disabled={saving}
        >
          {t('cancel')}
        </button>
        <button
          type="button"
          className={classes.save}
          onClick={handleSave}
          disabled={saving}
        >
          {saving
            ? t('saving')
            : manualKind === MANUAL_TIME_ENTRY_KIND.ABSENCE
              ? (editAbsenceRegId ? t('updateAbsence') : t('saveAbsence'))
              : (returnTo === 'dailySummary' ? 'Oppdater' : (isEditMode ? t('updateTime') : t('saveTime')))}
        </button>
      </div>

      <div className={classes.btnRow}>
        {(isEditMode || (manualKind === MANUAL_TIME_ENTRY_KIND.ABSENCE && editAbsenceRegId)) && (
          <button
            className={classes.deleteBtn}
            onClick={handleDelete}
            disabled={saving}
          >
            <span>
              {manualKind === MANUAL_TIME_ENTRY_KIND.ABSENCE
                ? t('deleteAbsence')
                : t('deleteTask')}
            </span>
          </button>
        )}
      </div>

      <Modal
        open={absenceWorkConfirmOpen}
        onClose={cancelAbsenceWorkConfirm}
        position="center"
        className={classes.absenceConfirmModal}
      >
        <div
          className={classes.absenceConfirmInner}
          role="dialog"
          aria-labelledby="absence-work-confirm-title"
          onClick={(e) => e.stopPropagation()}
        >
          <h2 id="absence-work-confirm-title" className={classes.absenceConfirmTitle}>
            {t('absenceSameDayDialogTitle')}
          </h2>
          <p className={classes.absenceConfirmText}>{t('absenceSameDayWorkWarning')}</p>
          <div className={classes.absenceConfirmActions}>
            <button type="button" className={classes.confirmCancelBtn} onClick={cancelAbsenceWorkConfirm}>
              {t('cancel')}
            </button>
            <button type="button" className={classes.confirmSaveBtn} onClick={confirmAbsenceWorkSave}>
              {t('absenceSameDayContinueSave')}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default AddForm;